import {StartNodeEditor} from "./StartNodeEditor.js";
import {EndNodeEditor} from "./EndNodeEditor.js";
import {LogNodeEditor} from "./LogNodeEditor.js";
import {WaitNodeEditor} from "./WaitNodeEditor.js";
import {XorGatewayEditor} from "./XorGatewayEditor.js";
import {ProcessNodeEditor} from "./ProcessNodeEditor.js";
import {EVENT_HEIGHT, EVENT_WIDTH} from "./EventEditor.js";
import {TASK_HEIGHT, TASK_WIDTH} from "./TaskEditor.js";
import {GATEWAY_HEIGHT, GATEWAY_WIDTH} from "./GatewayEditor.js";
import {EdgeEditor} from "./EdgeEditor.js";
import {SwimlaneEditor} from "./SwimlaneEditor.js";
import {OrGatewayEditor} from "./OrGatewayEditor.js";
import {AndGatewayEditor} from "./AndGatewayEditor.js";
import {EventGatewayEditor} from "./EventGatewayEditor.js";
import {NODE_REGISTRY, reg} from "./NodeRegistry.js";
import {msg} from "@/i18n/Message.ts";

/**
 * @typedef {Object} PNode
 * @property {string} id - unique ID of the node (per definition)
 * @property {string} class - Java class implementing this node
 * @property {string} [name] - short title
 * @property {string} [description] - longer description
 */
/**
 * @typedef {Object} PSwimlane
 * @property {string} id - unique ID of the swimlane (per definition)
 * @property {string} class - Java class implementing this swimlane
 * @property {string} [name] - short title
 * @property {string} [description] - longer description
 */
/** @typedef {Object} PEdge
 * @property {string} id - unique ID of the edge (per node)
 * @property {string} class - Java class implementing this edge
 * @property {string} [name] - short title
 * @property {string} [description] - longer description
 */
/** @typedef {Object} PConnection
 * @property {string} from - id of a node
 * @property {string} to - id of a node
 * @property {PEdge} edge - edge description
 */
/** @typedef {Object} MiddlePoint
 * @property {number} x
 * @property {number} y
 */
/** @typedef {Object} PConCoords
 * @property {MiddlePoint[]} mp - middle points (in relative coordinate system based on from-node and to-node
 */
/** @typedef {Object} PCoords
 * @property {number} x - X coordinate of the center
 * @property {number} y - Y coordinate of the center
 * @property {number} w - width
 * @property {number} h - height
 * @property {Object.<string, PConCoords>} [out] - object where properties are IDs of outgoing edges and values are {@link PConCoords}
 */
/** @typedef {Object} PSLCoords
 * @property {number} l - left coordinate
 * @property {number} t - top coordinate
 * @property {number} w - width
 * @property {number} h - height
 */
/** @typedef {Object} PModel
 * @property {PNode[]} nodes - list of nodes
 * @property {PSwimlane[]} lanes - list of swimlanes
 * @property {PConnection[]} connections - list of connections
 * @property {object[]} [params] - input parameters of the process
 * @property {Object.<string, PCoords>} [chart] - height
 * @property {Object.<string, PSLCoords>} [chartsl] - height
 */


export const NodeType = {
	START: "start",
	END: "end",
	EVENT: "event",
	GATEWAY: "gateway",
	TASK: "task",
	CALL: "call"
};

export class ProcessElement {
	/**
	 * @param {ProcessEditor} parent
	 * @param {PNode} node
	 * @param {PCoords} coords
	 * @return {ProcessNodeEditor}
	 */
	static newNodeEditor(parent, node, coords) {
		let ed = ProcessElement.editorFor(node.class);
		return new ed(parent, node, coords);
	}

	static editorFor(clazz) {
		switch (clazz) {
			case "com.anneca.intouch.workflow.node.StartNode":
				return StartNodeEditor;
			case "com.anneca.intouch.workflow.node.EndNode":
				return EndNodeEditor;
			case "com.anneca.intouch.workflow.node.LogNode":
				return  LogNodeEditor;
			case "com.anneca.intouch.workflow.node.WaitNode":
				return  WaitNodeEditor;
			case "com.anneca.intouch.workflow.node.IfThenNode":
			case "com.anneca.intouch.workflow.node.ExclusiveGatewayNode":
				return XorGatewayEditor;
			case "com.anneca.intouch.workflow.node.InclusiveGatewayNode":
				return OrGatewayEditor;
			case "com.anneca.intouch.workflow.node.ParallelGatewayNode":
				return AndGatewayEditor;
			case "com.anneca.intouch.workflow.node.EventGatewayNode":
				return EventGatewayEditor;
		}
		return ProcessNodeEditor;
	}

	/**
	 * @param {string} type
	 * @return {string}
	 */
	static getDefaultClassForNodeType(type) {
		switch (type) {
			case NodeType.START:
				return "com.anneca.intouch.workflow.node.StartNode";
			case NodeType.END:
				return "com.anneca.intouch.workflow.node.EndNode";
			case NodeType.EVENT:
				return "com.anneca.intouch.workflow.node.WaitNode";
			case NodeType.GATEWAY:
				return "com.anneca.intouch.workflow.node.ExclusiveGatewayNode";
			case NodeType.TASK:
				return "com.anneca.intouch.workflow.node.LogNode";
			case NodeType.CALL:
				return "com.anneca.intouch.workflow.node.LogNode";
		}
	}

	/**
	 * @param {ProcessEditor} parent
	 * @param {PSwimlane} swimlane
	 * @param {PSLCoords} coords
	 * @return {SwimlaneEditor}
	 */
	static newLaneEditor(parent, swimlane, coords) {
		if (typeof swimlane !== "object") throw "Missing lane data object";
		if (typeof coords !== "object") throw "Lane must have existing coords object";
		return new SwimlaneEditor(parent, swimlane, coords);
	}

	/**
	 * @param {ProcessEditor} parent
	 * @param {PConnection} connection
	 * @param {PCoords} fromCoords
	 * @param {PCoords} toCoords
	 * @return {EdgeEditor}
	 */
	static newEdgeEditor(parent, connection, fromCoords, toCoords) {
		return new EdgeEditor(parent, connection, fromCoords, toCoords);
	}

	/**
	 * @param {PNode} node
	 * @param {PCoords} coords
	 * @param {object} defaults
	 */
	static cleanUpCoords(node, coords, defaults) {
		const meta = NODE_REGISTRY.get(node.class);
		defaults.w = meta.width;
		defaults.h = meta.height;

		ProcessNodeEditor.cleanUpCoords(node, coords, defaults);
	}

	/**
	 * @param {PSwimlane} sl
	 * @param {PSLCoords} coords
	 * @param {object} defaults
	 */
	static cleanUpLaneCoords(sl, coords, defaults) {
		SwimlaneEditor.cleanUpCoords(sl, coords, defaults);
	}

	static getDefaultClassForLane() {
		return "com.anneca.intouch.workflow.lane.UserLane";
	}
}

export class ProcessNode extends ProcessElement {
	constructor(nodeType, clazz, minIn, maxIn, minOut, maxOut, width, height) {
		super();
		this.clazz = clazz;
		this.minIn = minIn;
		this.maxIn = maxIn;
		this.minOut = minOut;
		this.maxOut = maxOut;
		this.width = width;
		this.height = height;
	}

	get name() {
		const key = "process.node"+this.clazz.substring(this.clazz.lastIndexOf("."));
		return msg(key);
	}
}

class TaskNode extends ProcessNode {
	constructor(clazz) {
		super(NodeType.TASK, clazz, 1, null, 1, 1, TASK_WIDTH, TASK_HEIGHT);
	}
}

class CallNode extends ProcessNode {
	constructor(clazz) {
		super(NodeType.CALL, clazz, 1, null, 1, 1, TASK_WIDTH, TASK_HEIGHT);
	}
}

class EventNode extends ProcessNode {
	constructor(clazz) {
		super(NodeType.EVENT, clazz, 1, null, 1, 1, EVENT_WIDTH, EVENT_HEIGHT);
	}
}

class GatewayNode extends ProcessNode {
	constructor(clazz, minIn, maxIn, minOut, maxOut) {
		super(NodeType.GATEWAY, clazz, minIn, maxIn, minOut, maxOut, GATEWAY_WIDTH, GATEWAY_HEIGHT);
	}
}

reg(new ProcessNode(NodeType.START, "com.anneca.intouch.workflow.node.StartNode", 0, 0, 1, 1, EVENT_WIDTH, EVENT_HEIGHT));
reg(new ProcessNode(NodeType.END, "com.anneca.intouch.workflow.node.EndNode", 1, null, 0, 0, EVENT_WIDTH, EVENT_HEIGHT));

reg(new TaskNode("com.anneca.intouch.workflow.node.LogNode"));

reg(new EventNode("com.anneca.intouch.workflow.node.WaitNode"));

reg(new GatewayNode("com.anneca.intouch.workflow.node.IfThenNode", 1, null, 2, 2));
reg(new GatewayNode("com.anneca.intouch.workflow.node.ExclusiveGatewayNode", 1, null, 1, null));
reg(new GatewayNode("com.anneca.intouch.workflow.node.InclusiveGatewayNode", 1, null, 1, null));
reg(new GatewayNode("com.anneca.intouch.workflow.node.ParallelGatewayNode", 1, null, 1, null));
reg(new GatewayNode("com.anneca.intouch.workflow.node.EventGatewayNode", 1, null, 1, null));


const PARAMS_CACHE = new Map();

/**
 * Load (or get from cache) specified named list
 * @param {string} name name of the list as is required by server-side ListManager
 */
export function getElementParams(clazz) {
	let value = PARAMS_CACHE.get(clazz);
	if (typeof value === 'undefined') {
		let url = "ajax/processElementParams.jsp?class="+encodeURIComponent(clazz)+"&form=1";
		jQuery.ajax({
			async: false,
			url: url,
			success: function(data) {
				PARAMS_CACHE.set(clazz, data);
				value = data;
			}
		});
	}
	return value;
}
